import {
  Sparkles,
  MessageSquare,
  Image as ImageIcon,
  FileCheck2,
  DollarSign,
  ArrowUpRight,
  Mail,
  Eye,
} from "lucide-react";

const TIMELINE_ICONS = {
  claim_filed: { Icon: Sparkles, color: "text-[#1E3F33]", bg: "bg-[#1E3F33]/10" },
  message: { Icon: MessageSquare, color: "text-[#1E3F33]", bg: "bg-[#1E3F33]/10" },
  photo: { Icon: ImageIcon, color: "text-[#C25934]", bg: "bg-[#C25934]/10" },
  document: { Icon: FileCheck2, color: "text-blue-700", bg: "bg-blue-50" },
  estimate: { Icon: DollarSign, color: "text-emerald-700", bg: "bg-emerald-50" },
  status_change: { Icon: ArrowUpRight, color: "text-amber-700", bg: "bg-amber-50" },
  email: { Icon: Mail, color: "text-gray-600", bg: "bg-gray-100" },
  share_open: { Icon: Eye, color: "text-[#C25934]", bg: "bg-[#C25934]/10" },
};

/** Render a small human-readable duration: '32s', '4m 12s', '2h 11m', '3d 4h'. */
export function formatShortDuration(seconds) {
  if (seconds == null || seconds < 0) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h < 24) return mm ? `${h}h ${mm}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const hh = h % 24;
  return hh ? `${d}d ${hh}h` : `${d}d`;
}

/** Single timeline event row — icon + title + actor badge + summary + meta. */
export function TimelineItem({ event }) {
  const cfg = TIMELINE_ICONS[event.kind] || TIMELINE_ICONS.message;
  const { Icon } = cfg;
  return (
    <li className="relative" data-testid={`timeline-item-${event.kind}`}>
      <span
        className={`absolute -left-[42px] top-0 flex items-center justify-center w-8 h-8 rounded-full ring-4 ring-white ${cfg.bg}`}
      >
        <Icon className={`w-4 h-4 ${cfg.color}`} />
      </span>
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <p className="font-['Outfit'] font-semibold text-sm">{event.title}</p>
        {event.actor_role && (
          <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#F9F8F6] border border-[#E5E7EB] text-gray-600">
            {event.actor_role}
          </span>
        )}
        {event.kind === "estimate" && event.meta?.amount && (
          <span className="text-xs font-semibold text-emerald-700">
            ${Number(event.meta.amount).toLocaleString()}
          </span>
        )}
        {event.kind === "photo" && event.meta?.severity && (
          <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
            {event.meta.severity}
          </span>
        )}
        {event.kind === "share_open" && event.meta?.opened_after_label && (
          <span
            className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#C25934]/10 text-[#C25934] border border-[#C25934]/30"
            data-testid="share-open-delta-badge"
          >
            +{event.meta.opened_after_label}
          </span>
        )}
      </div>
      {event.summary && (
        <p className="text-sm text-gray-700 leading-relaxed">{event.summary}</p>
      )}
      {event.kind === "photo" && event.meta?.tag_str && (
        <p className="text-xs text-gray-500 mt-1">Tags: {event.meta.tag_str}</p>
      )}
      {event.kind === "share_open" && event.meta?.ip && (
        <p className="text-xs text-gray-500 mt-1">
          {event.meta.ip}
          {event.meta?.user_agent ? ` · ${event.meta.user_agent}` : ""}
        </p>
      )}
      <p className="text-xs text-gray-400 mt-1">
        {new Date(event.created_at).toLocaleString()}
        {event.actor ? ` · ${event.actor}` : ""}
      </p>
    </li>
  );
}
